<template>
  <div class="production-generator"></div>
</template>

<script setup lang="ts">
/**
 * Thin wrapper around useProductionFileEngine for the per-row "Download SVG"
 * flow: mount hidden → compose → inline vector logos → download → emit reset.
 * All generation logic lives in the engine (also used by the batch modal).
 */
import { onMounted } from 'vue';
import { showToast } from '@/composables/swal';
import {
  COMPOSE_DEFAULTS,
  useProductionFileEngine,
  type CustomLogoConfig,
  type CustomTextConfig,
  type LogoConfig,
  type PatternConfig,
  type SvgGroupConfig,
} from '@/composables/useProductionFileEngine';
import {
  convertBasicShapesToPaths,
  flattenSourceClipPaths,
} from '@/utils/productionFile/clipFlatten';
import { buildVectorizeLogoUrl, productionStorageUrl } from '@/utils/productionFile/endpoints';
import { inlineCustomLogoVectors } from '@/utils/productionFile/logoVectorize';
import {
  addHeaderLabelToComposite,
  computePanelLabelPlacements,
} from '@/utils/productionFile/panelSplitter';
import {
  resolveOutlineSpec,
  SIZE_LABEL_FONT_PT,
  SIZE_LABEL_MARGIN_PT,
  SIZE_LABEL_OUTLINE_PT,
} from '@/utils/productionFile/nesting';
import { finalizeCompositeForCorel } from '@/utils/productionFile/compositeFinish';
import { firstLoadedFont } from '@/utils/productionFile/labelGlyphs';
import type { FactoryNestingSettings } from '@/models/FactoryNestingSettings';
import type { CustomLogo } from '@/models/CustomLogo';
import { buildProductionLogoResolver } from '@/composables/useLogoSources';

const props = withDefaults(
  defineProps<{
    designUrl: string;
    sizesUrl?: string;
    safeZoneUrl?: string;
    canvasResolution?: number;
    containerWidth?: number;
    containerHeight?: number;
    twoDCanvasWidth?: number;
    twoDCanvasHeight?: number;
    groupPatterns: Record<string, PatternConfig>;
    logos?: LogoConfig[];
    custom_logos?: CustomLogoConfig[];
    texts?: CustomTextConfig[];
    svgGroups?: SvgGroupConfig[];
    sizeDownload: string;
    /** Roster quantity for the "<SIZE>-<qty>" header printed above the artwork. */
    quantity?: number;
    /** Factory nesting settings (laser outline widths); defaults when absent. */
    nestSettings?: FactoryNestingSettings | null;
    /**
     * `sizeDownload` IS the size the masterfile is drawn at, made on purpose
     * without grading (shared-design production files): no "_UNGRADED" in the
     * file name and no missing-sizes-file warning.
     */
    atlasSize?: boolean;
    /** Bug 22504: fit custom logos to their stored cm (designs saved before 2026-09-21). */
    fitLogosToStoredSize?: boolean;
  }>(),
  {
    quantity: 1,
    nestSettings: null,
    atlasSize: false,
    fitLogosToStoredSize: false,
    sizesUrl: '',
    safeZoneUrl: '',
    // Mirror the engine's defaults: 760/600 is the builder's artwork sizing
    // ratio. The old 512 here silently overrode the engine and printed every
    // name/number/logo at 0.85x of the builder.
    canvasResolution: COMPOSE_DEFAULTS.canvasResolution,
    containerWidth: COMPOSE_DEFAULTS.containerWidth,
    containerHeight: COMPOSE_DEFAULTS.containerHeight,
    twoDCanvasWidth: COMPOSE_DEFAULTS.twoDCanvasWidth,
    twoDCanvasHeight: COMPOSE_DEFAULTS.twoDCanvasHeight,
    logos: () => [],
    custom_logos: () => [],
    texts: () => [],
    svgGroups: () => [],
  }
);

const emit = defineEmits<{
  (e: 'resetDownloadProductionFile'): void;
  (e: 'error', message: string): void;
}>();

const engine = useProductionFileEngine();

onMounted(async () => {
  try {
    const result = await engine.compose({
      designUrl: props.designUrl,
      sizesUrl: props.sizesUrl || undefined,
      safeZoneUrl: props.safeZoneUrl || undefined,
      size: props.sizeDownload,
      canvasResolution: props.canvasResolution,
      containerWidth: props.containerWidth,
      containerHeight: props.containerHeight,
      twoDCanvasWidth: props.twoDCanvasWidth,
      twoDCanvasHeight: props.twoDCanvasHeight,
      groupPatterns: props.groupPatterns,
      logos: props.logos,
      custom_logos: props.custom_logos,
      texts: props.texts,
      svgGroups: props.svgGroups,
      fitLogosToStoredSize: props.fitLogosToStoredSize,
    });

    if (!result.sizeFound) {
      showToast(`There's no size "${props.sizeDownload}" in this SVG.`, 'error');
      return;
    }

    // Which vector each logo gets (auto trace from order placement, a designer's
    // manual svg, or the upload as it is) follows the Logo inspector's choice.
    const logoNotes: string[] = [];
    const resolveUrl = await buildProductionLogoResolver(
      props.custom_logos as unknown as CustomLogo[],
      logoNotes
    );
    const inlined = await inlineCustomLogoVectors(result.svg, result.customLogoKeys, {
      storageUrl: productionStorageUrl(),
      vectorizeUrl: buildVectorizeLogoUrl,
      cache: engine.cache.logoSvg,
      resolveUrl,
    });
    const unhonoured = logoNotes.filter((n) => /not-svg|missing/.test(n));
    if (unhonoured.length) {
      showToast(
        `${unhonoured.length} logo(s) couldn't be placed as chosen in the Logo inspector, so the automatic trace was used.`,
        'warning'
      );
    }

    // Source atlases mask panel art with silhouette clips, which CorelDRAW's
    // importer drops wholesale — bake them into plain intersection geometry.
    // It also mangles rects under the composite's flip transforms, so basic
    // shapes become equivalent <path>s.
    const flattened = convertBasicShapesToPaths((await flattenSourceClipPaths(inlined.svg)).svg);

    const ungraded = !props.atlasSize && result.warnings.includes('ungraded-base-size');
    const fileName = `design_${props.sizeDownload}${ungraded ? '_UNGRADED' : ''}.svg`;

    // Per-panel size labels, ink-anchored to the fabric (best effort).
    let finalSvg = flattened.svg;
    try {
      if (result.panels.length && props.sizeDownload.trim()) {
        const marginUnits =
          (SIZE_LABEL_MARGIN_PT * result.viewBoxSize) / (result.heightPt || result.viewBoxSize);
        const placements = await computePanelLabelPlacements(
          finalSvg,
          result.panels,
          result.overlays,
          result.viewBoxSize,
          marginUnits,
          { silhouetteViewBox: true }
        );
        // Anchor layer out, one PowerClip per panel (with its overlays and its
        // size indicator inside), the factory's laser outlines under the
        // artwork — same as the nested sheets.
        finalSvg = await finalizeCompositeForCorel(
          finalSvg,
          placements,
          resolveOutlineSpec(props.nestSettings),
          { viewBoxSize: result.viewBoxSize, widthPt: result.widthPt, heightPt: result.heightPt },
          {
            sizeText: props.sizeDownload,
            fontPt: SIZE_LABEL_FONT_PT,
            outlinePt: SIZE_LABEL_OUTLINE_PT,
            // Outlined with the row's own (already loaded) font so the label is
            // curves Corel clips into the PowerClip, not loose text.
            font: await firstLoadedFont(engine.cache.font),
          }
        );
      }
      // "<SIZE>-<qty>" header above the artwork, as on the factory's own sheets.
      if (props.sizeDownload.trim()) {
        finalSvg = addHeaderLabelToComposite(
          finalSvg,
          `${props.sizeDownload.toUpperCase()}-${props.quantity || 1}`
        );
      }
    } catch {
      /* keep the unlabeled composite rather than failing the download */
    }

    const blob = new Blob([finalSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);

    if (ungraded) {
      showToast('This product has no sizes file, so the file was generated ungraded at the base size.', 'warning');
    }
    if (inlined.warnings.length) {
      showToast(
        `${inlined.warnings.length} logo(s) couldn't be vectorized and stay as images.`,
        'warning'
      );
    }
  } catch (error) {
    console.error('Production file generation failed', error);
    showToast('The production files couldn\'t be generated. Try again.', 'error');
  } finally {
    emit('resetDownloadProductionFile');
  }
});
</script>
